import { useMemo, useCallback, useState } from 'react'
import type { JSX } from 'react'
import {
  NavLink,
  Outlet,
  useMatch,
  useNavigate,
  useParams,
} from 'react-router-dom'

import {
  Button,
  CompassIcon,
  DropdownMenu,
  EllipsisIcon,
  Flex,
  IconButton,
  PageHeader,
  PageLayout,
  PencilIcon,
  PlusIcon,
  toast,
  useIsMobileScreenSize,
} from '@buffer-mono/popcorn'
import { BufferTrackerReact, Client, Product } from '@buffer-mono/tracking-plan'

import {
  useAccount,
  useHasPaymentDetails,
  useIsOneBufferOrg,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import { range } from '~publish/helpers/range'
import { isOfType } from '~publish/helpers/typeGuards'
import { isFreeUser } from '~publish/helpers/user'
import { useInMemoryRestoreScroll } from '~publish/hooks/scroll/useRestoreScroll'
import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'
import { usePageTitle } from '~publish/hooks/usePageTitle'
import { navigateToBilling } from '~publish/helpers/navigateToBilling'
import { isNotUndefined } from '~publish/legacy/utils/isNotUndefined/isNotUndefined'

import { Limits } from '../../constants/constants'
import type { FeedCTA } from '../../constants/constants'
import { useCollectionListDetails } from '../../hooks/useCollectionListDetails'
import { parseErrorMessage, useCreateFeed } from '../../hooks/useCreateFeed'
import type { WrappedCreateFeedResponse } from '../../hooks/useCreateFeed'
import { useDeleteFeed } from '../../hooks/useDeleteFeed'
import { useDeleteFeedCollection } from '../../hooks/useDeleteFeedCollection'
import { useFeedItems } from '../../hooks/useFeedItems'
import { useFeedsForSidebar } from '../../hooks/useFeedsForSidebar'
import { useRefreshFeedCollection } from '../../hooks/useRefreshFeedCollection'
import { useUpdateFeedCollection } from '../../hooks/useUpdateFeedCollection'
import { CollectionListEmptyState } from '../CollectionList/CollectionListEmptyState'
import { ExploreFeedsDialog } from '../ExploreFeedsDialog/ExploreFeedsDialog'
import { ExploreFeedsDialogDropdownMenuItem } from '../ExploreFeedsDialog/ExploreFeedsDialogDropdownMenuItem'
import {
  FeedArticleCardSkeleton,
  FeedArticleLoadFailure,
} from '../FeedArticleList/FeedArticleCard'
import { FeedArticleList } from '../FeedArticleList/FeedArticleList'
import { FeedItemIcon } from '../FeedIcon/FeedIcon'
import { ImportFeedsDialogDropdownMenuItem } from '../ImportFeedsDialog/ImportFeedsDialogDropdownMenuItem'
import { ListHeaderIcon } from '../ListHeader/ListHeaderIcon'
import { ListHeaderTitleEditor } from '../ListHeaderV2/ListHeaderTitleEditor'
import { ListHeaderTitleV2 } from '../ListHeaderV2/ListHeaderTitleV2'
import { RefreshAction } from '../RefreshAction'
import { DeleteDropdownMenuItem } from '../DeleteConfirmationDialog/DeleteConfirmationDialog'
import { SubscribeToFeedDialogDropdownMenuItem } from '../SubscribeToFeedDialog/SubscribeToFeedDialogDropdownMenuItem'

import styles from './CollectionListV2.module.css'

export const CollectionListV2 = (): JSX.Element => {
  const { feedCollectionId } = useParams<{ feedCollectionId: string }>()
  const organizationId = useOrganizationId()
  const feedMatch = useMatch('/create/feeds/:feedId')
  const navigate = useNavigate()
  const openGlobalModal = useOpenGlobalModal()
  const isMobileScreenSize = useIsMobileScreenSize()
  const [isRenaming, setIsRenaming] = useState(false)
  const [deleteFeedCollection] = useDeleteFeedCollection()
  const [updateFeedCollection] = useUpdateFeedCollection()
  const pageLayoutScrollableRef = useInMemoryRestoreScroll(
    feedCollectionId ?? '',
  )

  const {
    collection,
    loading: loadingCollectionData,
    error: collectionListError,
  } = useCollectionListDetails({ feedCollectionId })

  const feedCollectionIds = useMemo(
    () => (feedCollectionId ? [feedCollectionId] : undefined),
    [feedCollectionId],
  )

  const {
    loading: loadingFeedItems,
    items,
    fetchingMore,
    refetch,
    lastElementRef,
    error: feedItemsError,
  } = useFeedItems({
    organizationId,
    feedCollectionIds,
  })

  // We need this to make sure the Empty state stays rendered while feeds are being
  // added inside of its dialogs, otherwise as soon as one feed is added the empty state
  // and dialog will stop being rendered
  const [isAddingFeeds, setIsAddingFeeds] = useState(false)
  const shouldRenderEmptyState =
    (!loadingCollectionData && !collection?.feeds?.length) || isAddingFeeds
  const handleEmptyStateDialogOpenChange = (open: boolean): void => {
    setIsAddingFeeds(open)
  }

  usePageTitle(collection?.name ? `${collection.name} (Feed)` : 'Feeds')

  const feeds = useMemo(
    () =>
      collection?.feeds.map((feed) => ({
        id: feed.id,
        name: feed.name,
        url: isOfType(feed.connectionMetadata, 'RSSFeedConnectionMetadata')
          ? feed.connectionMetadata?.url
          : undefined,
      })) ?? [],
    [collection?.feeds],
  )
  const existingFeeds = feeds
    .map((feed) => (feed.url ? { id: feed.id, url: feed.url } : undefined))
    .filter(isNotUndefined)

  /**
   * Calculate limits
   */
  const { account } = useAccount()
  const tier = isFreeUser(account) ? 'free' : 'paid'

  // Get the total number of feeds for the organization with cache-first to calculate remaining feeds
  const { totalFeeds } = useFeedsForSidebar({
    organizationId,
    fetchPolicy: 'cache-first',
  })
  const totalRemainingFeeds = Limits.feeds[tier].total - totalFeeds
  const collectionRemainingFeeds =
    Limits.feeds[tier].perCollection - feeds.length
  // If the total remaining feeds is less than the collection remaining feeds, use the total remaining feeds
  const remainingFeedsCount =
    collectionRemainingFeeds < totalRemainingFeeds
      ? collectionRemainingFeeds
      : totalRemainingFeeds

  // If the collection limit has not been reached, but the total feed limit has,
  // we still have to prevent adding a feed to the collection, so we have to
  // check which limit has been reached to properly inform the limit dialogs
  const totalLimitReached = totalFeeds >= Limits.feeds[tier].total
  const collectionLimitReached =
    feeds.length >= Limits.feeds[tier].perCollection
  const limitReachedType = collectionLimitReached
    ? 'feedspercollection'
    : 'feeds'

  /**
   * Event handlers
   */
  const isOneBufferOrg = useIsOneBufferOrg()
  const hasPaymentDetails = useHasPaymentDetails()
  const commonTrackingProperties = useCommonTrackingProperties()
  const baseTrackingProperties = useMemo(
    () => ({
      clientName: Product.Publish,
      product: Client.PublishWeb,
      hasPaymentDetails,
      isFreeUser: tier === 'free',
      ...commonTrackingProperties,
    }),
    [commonTrackingProperties, hasPaymentDetails, tier],
  )

  const handleUpgradeClick = ({
    cta,
    upgradePathName,
  }: {
    cta: CTA
    upgradePathName: string
  }): void => {
    const trackingProperties = {
      ...baseTrackingProperties,
      cta,
      upgradePathName,
    }
    if (isOneBufferOrg) {
      BufferTrackerReact.cTAClicked(trackingProperties)

      openGlobalModal(MODALS.planSelector, trackingProperties)
    } else {
      navigateToBilling(cta)
    }
  }

  const [createFeed] = useCreateFeed()
  const handleCreateFeed = useCallback(
    async ({
      feedUrl,
      cta,
    }: {
      feedUrl: string
      cta: FeedCTA
    }): Promise<WrappedCreateFeedResponse> => {
      if (!organizationId) {
        throw new Error('Organization ID is required')
      }

      const result = await createFeed({
        variables: {
          input: {
            organizationId,
            collectionId: collection?.id,
            rssAtom: { url: feedUrl },
          },
        },
      })

      if (result.success) {
        toast.success('Successfully added feed')
        BufferTrackerReact.feedSubscribed({
          product: 'publish',
          organizationId,
          feedId: result.data.feed.id,
          feedCollectionId: collection?.id ?? '',
          feedUrl,
          feedQuery: undefined,
          feedType: 'rssAtom',
          clientName: 'publishWeb',
          cta,
        })
        refetch()
        return { feedId: result.data.feed.id, error: null }
      }
      return { feedId: null, error: parseErrorMessage(result.error) }
    },
    [createFeed, organizationId, refetch, collection?.id],
  )

  const [deleteFeed] = useDeleteFeed()
  const handleDeleteFeed = useCallback(
    async (deletingFeedId: string) => {
      if (!organizationId) return

      const { error } = await deleteFeed({
        variables: { input: { organizationId, id: deletingFeedId } },
      })

      if (error) {
        toast.error(`Failed to delete feed`)
        return
      }

      // Only redirect if the deleted feed is the active one
      if (feedMatch?.params.feedId !== deletingFeedId) {
        return
      }
      if (collection?.id) {
        navigate('/create/feeds/collections/' + collection.id)
        return
      }
      navigate('/create/feeds/')
    },
    [
      deleteFeed,
      collection,
      organizationId,
      navigate,
      feedMatch?.params.feedId,
    ],
  )

  const handleDeleteCollection = useCallback(async () => {
    if (!organizationId || !feedCollectionId) return

    const { error } = await deleteFeedCollection({
      variables: {
        input: {
          organizationId,
          feedCollectionId,
        },
      },
    })

    if (error) {
      toast.error('Failed to delete collection')
      return
    }

    toast.success('Collection deleted successfully')
    navigate('/create/feeds')
  }, [deleteFeedCollection, organizationId, feedCollectionId, navigate])

  const handleRename = useCallback(
    async (newName: string) => {
      if (!organizationId || !feedCollectionId) return

      const { error } = await updateFeedCollection({
        variables: {
          input: {
            organizationId,
            feedCollectionId,
            name: newName,
          },
        },
      })

      if (error) {
        toast.error('Failed to rename collection')
        return
      }

      toast.success('Collection renamed successfully')
      setIsRenaming(false)
    },
    [updateFeedCollection, organizationId, feedCollectionId],
  )

  const [refreshFeedCollection] = useRefreshFeedCollection()
  const handleRefreshCollection = useCallback(async (): Promise<void> => {
    if (!organizationId || !feedCollectionId) return

    try {
      const { error } = await refreshFeedCollection({
        variables: {
          input: {
            feedCollectionId,
            organizationId,
          },
        },
      })
      if (!error) {
        toast.success('Your collection has been refreshed!')
      } else {
        toast.error('Failed to refresh collection')
      }
      // eslint-disable-next-line unused-imports/no-unused-vars
    } catch (_error) {
      toast.error('Failed to refresh collection')
    }
  }, [organizationId, feedCollectionId, refreshFeedCollection])

  const loadingState = useMemo(() => {
    if (collectionListError || feedItemsError)
      return {
        state: 'error',
        error: collectionListError || feedItemsError,
      } as const
    if (shouldRenderEmptyState) return { state: 'empty' } as const
    if ((loadingFeedItems || loadingCollectionData) && !items?.length)
      return { state: 'loading' } as const
    if (items && items.length > 0) return { state: 'loaded' } as const
    return { state: 'loading' } as const
  }, [
    shouldRenderEmptyState,
    loadingFeedItems,
    loadingCollectionData,
    items,
    collectionListError,
    feedItemsError,
  ])

  const lastUpdated = useMemo(() => {
    const timestamps = collection?.feeds.map((feed) => {
      return new Date(feed.lastRefreshed)
    })
    if (!timestamps) {
      return new Date()
    }
    timestamps.sort((a, b) => b.getTime() - a.getTime())
    return timestamps.length > 0 ? timestamps[0] : new Date()
  }, [collection?.feeds])

  const showRefreshAction =
    !isMobileScreenSize &&
    collection &&
    organizationId &&
    collection?.feeds?.length > 0

  return (
    <>
      <Outlet />
      <PageLayout className={styles.pageLayout} ref={pageLayoutScrollableRef}>
        <PageHeader separator={false} className={styles.narrowHeader}>
          <PageHeader.Row size="narrow">
            <Flex align="center" gap="sm">
              {isRenaming ? (
                <ListHeaderTitleEditor
                  defaultValue={collection?.name ?? ''}
                  icon={
                    <ListHeaderIcon
                      title={collection?.name ?? ''}
                      isCollection
                      isLoading={false}
                    />
                  }
                  onUpdate={handleRename}
                  onClose={(): void => setIsRenaming(false)}
                />
              ) : (
                <ListHeaderTitleV2
                  title={collection?.name ?? ''}
                  isCollection
                  isLoading={!collection}
                />
              )}
              {!isMobileScreenSize && collection && !isRenaming && (
                <DropdownMenu>
                  <DropdownMenu.Trigger>
                    <IconButton
                      label="View feeds"
                      size="medium"
                      variant="tertiary"
                    >
                      <EllipsisIcon />
                    </IconButton>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content align="start">
                    {collection.feeds.length > 0 && (
                      <>
                        {collection.feeds.map((feed) => {
                          const feedUrl = isOfType(
                            feed.connectionMetadata,
                            'RSSFeedConnectionMetadata',
                          )
                            ? feed.connectionMetadata.url
                            : undefined
                          return (
                            <DropdownMenu.Item key={feed.id} asChild>
                              <NavLink to={`/create/feeds/${feed.id}`}>
                                <FeedItemIcon
                                  feedName={feed.name}
                                  url={feedUrl}
                                />
                                {feed.name}
                              </NavLink>
                            </DropdownMenu.Item>
                          )
                        })}
                        <DropdownMenu.Separator />
                      </>
                    )}
                    <DropdownMenu.Sub>
                      <DropdownMenu.SubTrigger>
                        <PlusIcon />
                        Add to Collection
                      </DropdownMenu.SubTrigger>
                      <DropdownMenu.SubMenu>
                        <SubscribeToFeedDialogDropdownMenuItem
                          tier={tier}
                          type={limitReachedType}
                          limitReached={
                            collectionLimitReached || totalLimitReached
                          }
                          existingFeeds={existingFeeds}
                          onAddFeed={handleCreateFeed}
                          onUpgrade={handleUpgradeClick}
                        />
                        <ImportFeedsDialogDropdownMenuItem
                          tier={tier}
                          type={limitReachedType}
                          limitReached={
                            collectionLimitReached || totalLimitReached
                          }
                          existingFeeds={existingFeeds}
                          remainingFeedsCount={remainingFeedsCount}
                          onAddFeed={handleCreateFeed}
                          onUpgrade={handleUpgradeClick}
                          onRemoveFeed={handleDeleteFeed}
                        />
                        <DropdownMenu.Separator />
                        <ExploreFeedsDialogDropdownMenuItem
                          existingFeeds={existingFeeds}
                          tier={tier}
                          type={limitReachedType}
                          limitReached={
                            collectionLimitReached || totalLimitReached
                          }
                          onAddFeed={handleCreateFeed}
                          onUpgrade={handleUpgradeClick}
                          onRemoveFeed={handleDeleteFeed}
                        />
                      </DropdownMenu.SubMenu>
                    </DropdownMenu.Sub>
                    <DropdownMenu.Item
                      onClick={(): void => {
                        setIsRenaming(true)
                      }}
                    >
                      <PencilIcon />
                      Rename
                    </DropdownMenu.Item>
                    <DeleteDropdownMenuItem
                      mode="collection"
                      onConfirm={(): void => {
                        void handleDeleteCollection()
                      }}
                    />
                  </DropdownMenu.Content>
                </DropdownMenu>
              )}
            </Flex>
            <PageHeader.Actions>
              {showRefreshAction && (
                <RefreshAction
                  lastUpdated={lastUpdated}
                  onRefresh={handleRefreshCollection}
                />
              )}
            </PageHeader.Actions>
          </PageHeader.Row>
        </PageHeader>
        <PageLayout.Container size="narrow">
          {loadingState.state === 'empty' && (
            <CollectionListEmptyState>
              <ExploreFeedsDialog
                existingFeeds={existingFeeds}
                tier={tier}
                type={limitReachedType}
                limitReached={collectionLimitReached || totalLimitReached}
                onAddFeed={handleCreateFeed}
                onRemoveFeed={handleDeleteFeed}
                onOpenChange={handleEmptyStateDialogOpenChange}
                onUpgrade={(): void =>
                  handleUpgradeClick({
                    cta: 'create-feeds-upgradeDialog-upgradeButton-1',
                    upgradePathName: 'feeds-exploreFeeds-upgrade',
                  })
                }
              >
                <Button variant="secondary" size="large">
                  <CompassIcon /> Explore Feeds
                </Button>
              </ExploreFeedsDialog>
              <DropdownMenu>
                <DropdownMenu.Trigger>
                  <Button size="large" variant="primary">
                    <PlusIcon />
                    New Feed
                  </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content>
                  <SubscribeToFeedDialogDropdownMenuItem
                    existingFeeds={existingFeeds}
                    tier={tier}
                    type={limitReachedType}
                    buttonText="Add from URL"
                    limitReached={collectionLimitReached || totalLimitReached}
                    onAddFeed={handleCreateFeed}
                    onOpenChange={handleEmptyStateDialogOpenChange}
                    onUpgrade={handleUpgradeClick}
                  />
                  <ImportFeedsDialogDropdownMenuItem
                    existingFeeds={existingFeeds}
                    remainingFeedsCount={remainingFeedsCount}
                    tier={tier}
                    type={limitReachedType}
                    limitReached={collectionLimitReached || totalLimitReached}
                    onAddFeed={handleCreateFeed}
                    onRemoveFeed={handleDeleteFeed}
                    onOpenChange={handleEmptyStateDialogOpenChange}
                    onUpgrade={handleUpgradeClick}
                  />
                </DropdownMenu.Content>
              </DropdownMenu>
            </CollectionListEmptyState>
          )}
          {loadingState.state === 'loading' &&
            range(20).map((index) => <FeedArticleCardSkeleton key={index} />)}
          {loadingState.state === 'loaded' && (
            <FeedArticleList
              feedCollectionId={feedCollectionId}
              items={items}
              fetchingMore={fetchingMore}
              lastElementRef={lastElementRef}
            />
          )}
          {loadingState.state === 'error' && <FeedArticleLoadFailure />}
        </PageLayout.Container>
      </PageLayout>
    </>
  )
}
